import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SERVICES } from "@/lib/data";
import { Reveal, Overline } from "@/components/Reveal";

export default function Services() {
  const [active, setActive] = useState(0);
  const current = SERVICES[active];

  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 lg:py-40 bg-[var(--surface-subtle)]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <Reveal>
              <Overline testid="services-overline">What We Do</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl">
                Three crafts, one seamless celebration.
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          <div className="flex flex-col divide-y divide-[var(--border-gold)] border-y border-[var(--border-gold)]">
            {SERVICES.map((s, i) => (
              <button
                key={s.id}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                className="text-left py-7 group transition-colors"
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start gap-5">
                  <span
                    className={`font-accent text-sm pt-2 transition-colors ${
                      active === i ? "text-[var(--gold)]" : "text-[var(--ink-3)]"
                    }`}
                  >
                    {s.index}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <h3
                        className={`font-serif text-2xl sm:text-3xl transition-colors ${
                          active === i ? "text-[var(--ink)]" : "text-[var(--ink-2)]"
                        }`}
                      >
                        {s.title}
                      </h3>
                      <Plus
                        size={20}
                        className={`shrink-0 transition-all duration-500 ${
                          active === i ? "rotate-45 text-[var(--gold)]" : "text-[var(--ink-3)]"
                        }`}
                      />
                    </div>
                    <AnimatePresence initial={false}>
                      {active === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="mt-4 text-[var(--ink-2)] font-light leading-relaxed max-w-md">
                            {s.blurb}
                          </p>
                          <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                            {s.points.map((p) => (
                              <li key={p} className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
                                <span className="h-1 w-1 rounded-full bg-[var(--gold)]" />
                                {p}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="relative overflow-hidden rounded-[2px] min-h-[420px] lg:min-h-full">
            <AnimatePresence mode="wait">
              <motion.img
                key={current.id}
                src={current.image}
                alt={current.title}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/40 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="font-accent text-xs tracking-[0.3em] text-white/90">
                {current.index} · {current.title}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
